import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Users as UsersIcon, Trash2, Shield, Edit } from 'lucide-react';
import { canDelete } from '@/utils/permissions';

const ROLES = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Inventory Manager', label: 'Inventory Manager' },
  { value: 'Website Manager', label: 'Website Manager' }
];

const PERMISSIONS = [
  { value: 'pos', label: 'POS System' },
  { value: 'inventory', label: 'Inventory Management' },
  { value: 'costing', label: 'Costing' },
  { value: 'quotations', label: 'Quotations' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'orders', label: 'Orders' },
  { value: 'sales_history', label: 'Sales History' },
  { value: 'credit_invoices', label: 'Credit Invoices' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'loans', label: 'Loans' },
  { value: 'transfers', label: 'Transfers' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'settle_expenses', label: 'Settle Expenses' },
  { value: 'purchase_orders', label: 'View Purchase Orders' },
  { value: 'edit_po', label: 'Edit Purchase Orders' },
  { value: 'suppliers', label: 'Suppliers' },
  { value: 'budget', label: 'Budget' },
  { value: 'tax', label: 'Tax Management' },
  { value: 'pettycash_create', label: 'Create Petty Cash Requests' },
  { value: 'pettycash_approve_verify', label: 'Approve & Verify Petty Cash' },
  { value: 'customers', label: 'Customers' },
  { value: 'website', label: 'Website CMS' },
  { value: 'reports', label: 'Reports' },
  { value: 'settings', label: 'Settings' },
  { value: 'users', label: 'User Management' }
];

export default function UserManagement() {
  const { token, user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: '',
    permissions: []
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Submitting user with permissions:', formData.permissions);

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          full_name: formData.full_name,
          role: formData.role,
          permissions: formData.permissions
        };
        
        console.log('Updating user with data:', updateData);
        
        // Only include password if it was provided
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await axios.put(`${API}/users/${editingUser.email}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated successfully');
      } else {
        // Create new user
        console.log('Creating new user with data:', formData);
        const response = await axios.post(`${API}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('User created, response:', response.data);
        toast.success('User created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error creating/updating user:', error.response?.data);
      toast.error(error.response?.data?.detail || `Failed to ${editingUser ? 'update' : 'create'} user`);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't show existing password
      full_name: user.full_name,
      role: user.role,
      permissions: user.permissions || []
    });
    setShowDialog(true);
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const togglePermission = (permission) => {
    const newPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter(p => p !== permission)
      : [...formData.permissions, permission];
    setFormData({ ...formData, permissions: newPermissions });
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: '',
      permissions: []
    });
    setEditingUser(null);
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="user-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-600 mt-1">{users.length} users in system</p>
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-user-button">
                <Plus className="w-5 h-5 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" data-testid="user-form">
                <div className="overflow-y-auto flex-1 space-y-4 pr-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                        data-testid="user-fullname-input"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required={!editingUser}
                        disabled={!!editingUser}
                        data-testid="user-email-input"
                        className={editingUser ? 'bg-slate-100' : ''}
                      />
                    </div>
                    <div>
                      <Label>Password {editingUser ? '(Leave blank to keep current)' : '*'}</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingUser}
                        data-testid="user-password-input"
                        placeholder={editingUser ? 'Leave blank to keep current password' : ''}
                      />
                    </div>
                    <div>
                      <Label>Role *</Label>
                      <Select 
                        value={formData.role} 
                        onValueChange={(value) => {
                          // Only auto-assign permissions for Super Admin
                          if (value === 'Super Admin') {
                            // Super Admin gets all permissions
                            const allPermissions = PERMISSIONS.map(p => p.value);
                            setFormData({ ...formData, role: value, permissions: allPermissions });
                          } else {
                            // For Admin and other roles, keep current permissions
                            // User must manually select permissions
                            setFormData({ ...formData, role: value });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-3 block">Permissions</Label>
                    <div className="space-y-3">
                      {PERMISSIONS.map((permission) => (
                        <div key={permission.value} className="flex items-center space-x-3">
                          <Checkbox
                            id={permission.value}
                            checked={formData.permissions.includes(permission.value)}
                            onCheckedChange={() => togglePermission(permission.value)}
                          />
                          <label
                            htmlFor={permission.value}
                            className="text-sm font-medium cursor-pointer text-slate-700"
                          >
                            {permission.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t mt-4">
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-user-button">
                    {editingUser ? 'Save Changes' : 'Create User'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {users.map((user) => (
            <Card key={user.id} className="p-6 hover:shadow-lg transition-all" data-testid={`user-card-${user.email}`}>
              <div className="flex justify-between items-start">
                <div className="flex gap-4 flex-1">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                    {user.full_name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900">{user.full_name}</h3>
                    <p className="text-slate-600">{user.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-600">{user.role}</span>
                    </div>
                    {user.permissions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {user.permissions.includes('all') ? (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                            All Permissions
                          </span>
                        ) : (
                          user.permissions.map((perm) => (
                            <span key={perm} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                              {PERMISSIONS.find(p => p.value === perm)?.label || perm}
                            </span>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {user.role !== 'Super Admin' && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(user)}
                      className="text-blue-600 hover:bg-blue-50"
                      data-testid={`edit-user-${user.email}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    {canDelete(currentUser) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`delete-user-${user.email}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}